<template>
  <f7-page name="catalog-importer">
    <f7-navbar :title="$t('catalogImport.title') || 'Import catalog'" back-link="Back" />
<AppLayout>
  <template #header>
 
    <f7-block>
      <f7-segmented strong round raised>
        <f7-button round raised :active="source === 'FILE'" @click="source = 'FILE'">CSV / Excel</f7-button>
        <f7-button  round raised:active="source === 'GOOGLE'" @click="source = 'GOOGLE'">Google Sheet</f7-button>
        <f7-button round raised :active="source === 'MANUAL'" @click="source = 'MANUAL'">Manual</f7-button>
      </f7-segmented>
    </f7-block>

    <f7-block>
      <f7-progressbar :progress="progress" />
      <div class="step-label">{{ stepLabel }}</div>
    </f7-block>

    <!-- 1. Source -->
    <f7-block v-if="step === 'SOURCE'">
      <h2>{{ $t('catalogImport.chooseSource') || 'Choose a source' }}</h2>
      <p class="text-color-gray">{{ $t('catalogImport.sourceDesc') || 'Import listings and products, then review and edit them before anything is published.' }}</p>

      <template v-if="source === 'FILE'">
        <f7-button outline round raised @click="downloadTemplate">{{ $t('catalogImport.downloadTemplate') || 'Download template' }}</f7-button>
        <input ref="fileInput" class="hidden-input" type="file" accept=".csv,.xlsx,.xls" @change="readFile" />
        <div class="drop-zone" @click="fileInput?.click()">
          <f7-icon f7="cloud_upload" size="36" color="blue" />
          <strong>{{ $t('catalogImport.chooseFile') || 'Choose CSV or Excel' }}</strong>
          <small>{{ $t('catalogImport.maxSize', { mb: maxFileMb }) || `Maximum ${maxFileMb} MB` }}</small>
        </div>
        <p v-if="sourceName" class="text-color-blue">{{ sourceName }}</p>
      </template>

      <template v-if="source === 'GOOGLE'">
        <f7-list form inset>
          <f7-list-input v-model="googleUrl" type="url" :label="$t('catalogImport.googleUrl') || 'Google Sheet URL'" placeholder="https://docs.google.com/spreadsheets/d/..." />
          <f7-list-input v-model="googleTab" type="text" :label="$t('catalogImport.tabName') || 'Tab name'" :placeholder="$t('catalogImporter.productsPlaceholder')" />
        </f7-list>
        <f7-button fill round raised :disabled="!googleUrl || loading" @click="readGoogleSheet">{{ $t('catalogImport.loadGoogle') || 'Load Google Sheet' }}</f7-button>
      </template>

      <template v-if="source === 'MANUAL'">
        <f7-button raised round fill @click="addRow">{{ $t('catalogImport.addRow') || 'Add product row' }}</f7-button>
        <f7-block-title>{{ $t('common.rows') || 'Rows' }}</f7-block-title>
        <f7-card v-for="(row, index) in manualRows" :key="row.clientId">
          <f7-card-header><strong>{{ $t('catalogImport.rowNum', { num: index + 1 }) || `Row ${index + 1}` }}</strong><f7-link color="red" @click="removeRow(index)">{{ $t('common.delete') || 'Remove' }}</f7-link></f7-card-header>
          <f7-card-content><CatalogRowEditor :model-value="manualRows[index]" @update:model-value="manualRows[index] = $event" /></f7-card-content>
        </f7-card>
      </template>
    </f7-block>

    <!-- 2. Mapping -->
    <f7-block v-if="step === 'MAP'">
      <h2>{{ $t('catalogImport.mapColumns') || 'Map columns' }}</h2>
      <p class="text-color-gray">{{ $t('catalogImport.mapDesc') || 'Match your source columns to Avocash fields. Product rows with the same slug or product name are grouped.' }}</p>
      <f7-list form inset>
        <f7-list-item v-for="field in mappingFields" :key="field.key" :title="field.label" :after="field.required ? ($t('common.required') || 'Required') : ($t('common.optional') || 'Optional')">
            <select v-model="mapping[field.key]" type="select">
              <option value="">{{ $t('catalogImport.notSupplied') || 'Not supplied' }}</option>
              <option v-for="header in headers" :key="header" :value="header">{{ header }}</option>
            </select>
        </f7-list-item>
      </f7-list>
      <f7-button fill round raised :disabled="!requiredMappingComplete" @click="normalizeRows">{{ $t('catalogImport.previewProducts') || 'Preview products' }}</f7-button>
    </f7-block>

    <!-- 3. Review -->
    <f7-block v-if="step === 'REVIEW'">
      <h2>{{ $t('catalogImport.reviewEdit') || 'Review and edit' }}</h2>
      <f7-card>
        <f7-card-content>
          <div class="summary-grid">
            <div><strong>{{ products.length }}</strong><small>{{ $t('catalogImport.products') || 'Products' }}</small></div>
            <div><strong>{{ variantCount }}</strong><small>{{ $t('catalogImport.variants') || 'Variants' }}</small></div>
            <div class="text-color-red"><strong>{{ errorCount }}</strong><small>{{ $t('catalogImport.errors') || 'Errors' }}</small></div>
          </div>
        </f7-card-content>
      </f7-card>

      <f7-list strong inset>
        <f7-list-item
          v-for="(product, index) in products"
          :key="product.clientId"
          :title="product.name"
          :subtitle="`${product.variants.length} variants · ${product.listingMode}`"
          link
          @click="editProduct(index)"
        >
          <template #after>
            <f7-badge :color="product.errors.length ? 'red' : 'green'">
              {{ product.errors.length ? product.errors.length + ' errors' : ('Ready') }}
            </f7-badge>
          </template>
        </f7-list-item>
      </f7-list>

      <f7-block>
        <f7-button raised round outline @click="addMediaToSelected">{{ $t('catalogImport.addMedia')  }}</f7-button>
        <f7-button raised round fill @click="dryRun">{{ $t('catalogImport.validateServer')  }}</f7-button>
      </f7-block>
    </f7-block>

    <!-- 4. Edit product popup -->
    <AdaptiveSheet v-model:opened="editorOpened" swipe-to-close close-on-escape :tablet-fullscreen="false" close-by-outside-click>
      <f7-page>
        <f7-navbar :title="$t('catalogImport.editProduct') ">
          <f7-nav-right><f7-link sheet-close popup-close><f7-icon ios="f7:multiply" md="material:close" /></f7-link></f7-nav-right>
        </f7-navbar>
        <f7-block v-if="editingProduct">
          <f7-list form inset>
            <f7-list-input v-model="editingProduct.name" :label="$t('common.name') || 'Product name'" />
            <f7-list-input v-model="editingProduct.slug" :label="$t('common.slug') || 'Slug'" />
            <f7-list-input v-model="editingProduct.description" :label="$t('common.description') || 'Description'" type="textarea" />
            <f7-list-input v-model.number="editingProduct.basePrice" :label="$t('shopproductaddnew.price') || 'Base price'" type="number" />
            <f7-list-input v-model.number="editingProduct.memberPrice" :label="$t('shopproductaddnew.memberPrice') || 'Member price'" type="number" />
            <f7-list-input v-model.number="editingProduct.promotionPrice" :label="$t('shopproductaddnew.salePrice') || 'Promotion price'" type="number" />
            <f7-list-item :title="$t('shopproductaddnew.makeThePriceNegotiable') || 'Negotiable'"><template #after><f7-toggle v-model="editingProduct.negotiable" /></template></f7-list-item>
            <f7-list-item :title="$t('shopproductaddnew.groupAndFriendsBuy') || 'Group buying enabled'"><template #after><f7-toggle v-model="editingProduct.groupBuyEnabled" /></template></f7-list-item>
          </f7-list>

          <f7-block-title>{{ $t('shopproductaddnew.addProductImages') || 'Product media' }}</f7-block-title>
          <input type="file" multiple accept="image/*,video/*" @change="selectProductMedia" />
          <div class="media-grid">
            <div v-for="media in editingProduct.media" :key="media.clientId" class="media-tile">
              <img v-if="media.mediaType === 'IMAGE'" :src="media.previewUrl || media.url" />
              <f7-badge v-if="media.mainImage" color="green">Main</f7-badge>
              <f7-link color="red" @click="removeMedia(editingProduct, media.clientId)">{{ $t('common.delete') || 'Remove' }}</f7-link>
            </div>
          </div>

          <f7-block-title>{{ $t('shopproductaddnew.addProductVariants') || 'Variants' }}</f7-block-title>
          <f7-button round raised outline @click="addVariant(editingProduct)">{{ $t('shopproductaddnew.addProductVariants') || 'Add variant' }}</f7-button>
          <f7-card v-for="(variant, vIndex) in editingProduct.variants" :key="variant.clientId">
            <f7-card-content>
              <CatalogVariantEditor :model-value="variant" @update:model-value="editingProduct.variants[vIndex] = $event" @media="selectVariantMedia(variant, $event)" @remove="removeVariant(editingProduct, variant.clientId)" />
            </f7-card-content>
          </f7-card>
        </f7-block>
      </f7-page>
    </AdaptiveSheet>

    <!-- 5. Server validation -->
    <f7-block v-if="step === 'SERVER_RESULT'">
      <h2>{{ $t('catalogImport.serverValidation') || 'Server validation' }}</h2>
      <f7-list strong inset>
        <f7-list-item v-for="result in serverResults" :key="result.clientId" :title="result.name" :subtitle="result.message">
          <template #after><f7-badge :color="result.valid ? 'green' : 'red'">{{ result.valid ? 'Valid' : 'Fix' }}</f7-badge></template>
        </f7-list-item>
      </f7-list>
      <f7-button fill :disabled="serverErrorCount > 0 || committing" @click="commitImport">{{ $t('catalogImport.confirmUpload') || 'Confirm and upload' }}</f7-button>
    </f7-block>

    <!-- 6. Result -->
    <f7-block v-if="step === 'RESULT'" strong inset>
      <h2>{{ $t('catalogImport.importComplete') || 'Import complete' }}</h2>
      <p>{{ $t('catalogImport.createdCount', { count: importResult?.created || 0 }) || `${importResult?.created || 0} products created.` }}</p>
      <p>{{ $t('catalogImport.updatedCount', { count: importResult?.updated || 0 }) || `${importResult?.updated || 0} products updated.` }}</p>
      <p>{{ $t('catalogImport.failedCount', { count: importResult?.failed || 0 }) || `${importResult?.failed || 0} products failed.` }}</p>
      <f7-button raised round fill @click="finish">{{ $t('common.done') || 'Done' }}</f7-button>
    </f7-block>

    <f7-block v-if="step === 'SOURCE' || step === 'MAP' || step === 'REVIEW'" class="footer-actions">
      <f7-button round raised outline :disabled="step === 'SOURCE'" @click="previousStep">{{ $t('common.back') || 'Back' }}</f7-button>
      <f7-button fill raised round :disabled="!canContinue" @click="nextStep">{{ $t('common.continue') || 'Continue' }}</f7-button>
    </f7-block>
     </template>
</AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import AppLayout from '@/components/common/AppLayout.vue'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { f7 } from 'framework7-vue'
import { catalogApi } from '@/services/catalogApi'
import CatalogRowEditor from '@/components/catalog/CatalogRowEditor.vue'
import AdaptiveSheet from '../components/common/AdaptiveSheet.vue'
import CatalogVariantEditor from '@/components/catalog/CatalogVariantEditor.vue'
import i18n from '@/js/i18n'

const props = defineProps({ shopId: { type: String, required: true }, f7route: Object, f7router: Object })
const { t } = i18n.global

const source = ref('FILE')
const step = ref('SOURCE')
const sourceName = ref('')
const fileInput = ref(null)
const maxFileMb = 10
const googleUrl = ref('')
const googleTab = ref('Products')
const loading = ref(false)
const committing = ref(false)
const headers = ref([])
const rawRows = ref([])
const products = ref([])
const manualRows = ref([newRawRow()])
const serverResults = ref([])
const importResult = ref(null)
const editingIndex = ref(null)
const editorOpened = ref(false)

const mapping = reactive({
  listingMode: '', shopId: '', listingId: '', productName: '', slug: '', description: '',
  listingType: '', currency: '', basePrice: '', memberPrice: '', promotionPrice: '',
  promotionStartsAt: '', promotionEndsAt: '', groupBuyEnabled: '', negotiable: '',
  variantSku: '', variantName: '', variantAttributes: '', stockQuantity: '',
  mainImageUrl: '', variantImageUrls: '', groupBuyTiers: ''
})

const mappingFields = [
  { key: 'listingMode', label: 'Listing mode', required: false },
  { key: 'shopId', label: 'Shop ID', required: true },
  { key: 'listingId', label: 'Existing listing ID', required: false },
  { key: 'productName', label: 'Product name', required: true },
  { key: 'slug', label: 'Slug', required: false },
  { key: 'description', label: 'Description', required: false },
  { key: 'listingType', label: 'Listing type', required: true },
  { key: 'currency', label: 'Currency', required: true },
  { key: 'basePrice', label: 'Base price', required: true },
  { key: 'memberPrice', label: 'Member price', required: false },
  { key: 'promotionPrice', label: 'Promotion price', required: false },
  { key: 'promotionStartsAt', label: 'Promotion starts', required: false },
  { key: 'promotionEndsAt', label: 'Promotion ends', required: false },
  { key: 'groupBuyEnabled', label: 'Group buying enabled', required: false },
  { key: 'negotiable', label: 'Negotiable', required: false },
  { key: 'variantSku', label: 'Variant SKU', required: false },
  { key: 'variantName', label: 'Variant name', required: false },
  { key: 'variantAttributes', label: 'Variant attributes JSON', required: false },
  { key: 'stockQuantity', label: 'Stock quantity', required: false },
  { key: 'mainImageUrl', label: 'Main image URL', required: false },
  { key: 'variantImageUrls', label: 'Variant image URLs', required: false },
  { key: 'groupBuyTiers', label: 'Group-buy tiers', required: false }
]

const requiredMappingComplete = computed(() => ['shopId', 'productName', 'listingType', 'currency', 'basePrice'].every(k => mapping[k]))
const variantCount = computed(() => products.value.reduce((n, p) => n + p.variants.length, 0))
const errorCount = computed(() => products.value.reduce((n, p) => n + p.errors.length, 0) + products.value.reduce((n, p) => n + p.variants.reduce((m, v) => m + v.errors.length, 0), 0))
const serverErrorCount = computed(() => serverResults.value.filter(r => !r.valid).length)
const editingProduct = computed(() => editingIndex.value == null ? null : products.value[editingIndex.value])
const canContinue = computed(() => {
  if (step.value === 'SOURCE') return source.value === 'MANUAL' ? manualRows.value.length > 0 : rawRows.value.length > 0
  if (step.value === 'MAP') return requiredMappingComplete.value
  if (step.value === 'REVIEW') return products.value.length > 0
  return false
})
const progress = computed(() => ({ SOURCE: 16, MAP: 33, REVIEW: 50, SERVER_RESULT: 75, RESULT: 100 }[step.value]))
const stepLabel = computed(() => ({ SOURCE: t('catalogImport.stepSource') || 'Source', MAP: t('catalogImport.stepMap') || 'Map columns', REVIEW: t('catalogImport.stepReview') || 'Review and edit', SERVER_RESULT: t('catalogImport.stepServer') || 'Server validation', RESULT: t('catalogImport.stepComplete') || 'Complete' }[step.value]))

function newRawRow() {
  return {
    clientId: crypto.randomUUID(), listingMode: 'PRODUCT', shopId: props.shopId, productName: '', slug: '', description: '',
    listingType: 'PRODUCT', currency: 'EUR', basePrice: '', memberPrice: '', promotionPrice: '',
    promotionStartsAt: '', promotionEndsAt: '', groupBuyEnabled: false, negotiable: false,
    variantSku: '', variantName: 'Default', variantAttributes: '{}', stockQuantity: '',
    mainImageUrl: '', variantImageUrls: '', groupBuyTiers: ''
  }
}

function readFile(e) {
  const file = e.target.files?.[0]
  if (!file) return
  if (file.size > maxFileMb * 1024 * 1024) return f7.dialog.alert(t('catalogImporter.fileTooLarge'))
  sourceName.value = file.name
  file.name.toLowerCase().endsWith('.csv') ? parseCsv(file) : parseExcel(file)
}

function parseCsv(file) {
  Papa.parse(file, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: h => h.trim(),
    complete: r => {
      headers.value = r.meta.fields || []
      rawRows.value = r.data
      autoMap()
      step.value = 'MAP'
    }
  })
}

async function parseExcel(file) {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true })
  headers.value = []
  rawRows.value = []
  const sheet = wb.Sheets[wb.SheetNames[0]]
  rawRows.value = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false })
  headers.value = Object.keys(rawRows.value[0] || {})
  autoMap()
  step.value = 'MAP'
}

function autoMap() {
  mappingFields.forEach(f => {
    mapping[f.key] = headers.value.find(h => normalize(h) === normalize(f.key) || normalize(h) === normalize(f.label)) || ''
  })
}

function normalize(v) {
  return String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function normalizeRows() {
  products.value = source.value === 'MANUAL' ? groupRows(manualRows.value) : groupRows(rawRows.value.map((r, i) => mapRow(r, i)))
  step.value = 'REVIEW'
}

function mapRow(row, index) {
  const v = {}
  Object.keys(mapping).forEach(k => {
    v[k] = mapping[k] ? row[mapping[k]] : ''
  })
  return { ...v, clientId: `row-${index}-${Date.now()}` }
}

function groupRows(rows) {
  const groups = new Map()
  rows.forEach((r, i) => {
    const key = String(r.slug || r.productName || `row-${i}`).trim().toLowerCase()
    if (!groups.has(key)) {
      groups.set(key, {
        clientId: crypto.randomUUID(),
        listingMode: r.listingMode || 'PRODUCT',
        shopId: r.shopId || props.shopId,
        listingId: r.listingId || null,
        name: r.productName,
        slug: r.slug || slugify(r.productName),
        description: r.description || '',
        listingType: r.listingType || 'PRODUCT',
        currency: r.currency || 'EUR',
        basePrice: numberOrNull(r.basePrice),
        memberPrice: numberOrNull(r.memberPrice),
        promotionPrice: numberOrNull(r.promotionPrice),
        promotionStartsAt: r.promotionStartsAt || null,
        promotionEndsAt: r.promotionEndsAt || null,
        groupBuyEnabled: bool(r.groupBuyEnabled),
        negotiable: bool(r.negotiable),
        media: r.mainImageUrl ? [{ clientId: crypto.randomUUID(), mediaType: 'IMAGE', url: r.mainImageUrl, mainImage: true, sortOrder: 0 }] : [],
        variants: [],
        errors: []
      })
    }
    const p = groups.get(key)
    p.variants.push({
      clientId: crypto.randomUUID(),
      sku: r.variantSku || `${p.slug}-default`,
      name: r.variantName || 'Default',
      attributesJson: r.variantAttributes || '{}',
      stockQuantity: numberOrNull(r.stockQuantity),
      images: (r.variantImageUrls || '').split('|').filter(Boolean).map((url, j) => ({
        clientId: crypto.randomUUID(), url, mediaType: 'IMAGE', mainImage: j === 0, sortOrder: j
      })),
      errors: []
    })
  })
  const result = [...groups.values()]
  result.forEach(validateProduct)
  return result
}

function validateProduct(p) {
  p.errors = []
  if (!p.shopId) p.errors.push('Shop is required')
  if (!p.name) p.errors.push('Product name is required')
  if (!p.currency) p.errors.push('Currency is required')
  if (p.basePrice == null || p.basePrice < 0) p.errors.push('Base price is invalid')
  if (!p.media.some(m => m.mainImage)) p.errors.push('Main image is required')
  const skus = new Set()
  p.variants.forEach(v => {
    v.errors = []
    if (!v.sku) v.errors.push('SKU required')
    if (skus.has(v.sku)) v.errors.push('Duplicate SKU')
    skus.add(v.sku)
    if (v.stockQuantity != null && v.stockQuantity < 0) v.errors.push('Stock invalid')
  })
}

function editProduct(i) {
  editingIndex.value = i
  editorOpened.value = true
}

function addRow() {
  manualRows.value.push(newRawRow())
}

function removeRow(i) {
  manualRows.value.splice(i, 1)
}

function addVariant(p) {
  p.variants.push({
    clientId: crypto.randomUUID(),
    sku: `${p.slug}-${p.variants.length + 1}`,
    name: 'New variant',
    attributesJson: '{}',
    stockQuantity: 0,
    images: [],
    errors: []
  })
}

function removeVariant(p, id) {
  p.variants = p.variants.filter(v => v.clientId !== id)
  validateProduct(p)
}

function removeMedia(p, id) {
  p.media = p.media.filter(m => m.clientId !== id)
  validateProduct(p)
}

async function selectProductMedia(e) {
  if (!editingProduct.value) return
  for (const file of Array.from(e.target.files || [])) {
    const previewUrl = URL.createObjectURL(file)
    editingProduct.value.media.push({
      clientId: crypto.randomUUID(),
      file,
      previewUrl,
      mediaType: file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE',
      mainImage: editingProduct.value.media.length === 0,
      sortOrder: editingProduct.value.media.length
    })
  }
  validateProduct(editingProduct.value)
}

function selectVariantMedia(variant, files) {
  files.forEach((file, i) => {
    variant.images.push({
      clientId: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      mediaType: 'IMAGE',
      mainImage: variant.images.length === 0 && i === 0,
      sortOrder: variant.images.length
    })
  })
}

function addMediaToSelected() {
  if (products.value.length) editProduct(0)
}

function previousStep() {
  if (step.value === 'MAP') step.value = 'SOURCE'
  else if (step.value === 'REVIEW') step.value = 'MAP'
}

function nextStep() {
  if (step.value === 'SOURCE') {
    if (source.value === 'MANUAL') normalizeRows()
    else step.value = 'MAP'
  } else if (step.value === 'MAP') {
    normalizeRows()
  } else if (step.value === 'REVIEW') {
    dryRun()
  }
}

async function dryRun() {
  products.value.forEach(validateProduct)
  if (errorCount.value) return f7.dialog.alert(t('catalogImporter.fixErrorsBeforeSubmit'))
  const result = await catalogApi.dryRun({ shopId: props.shopId, products: products.value.map(toApiProduct) })
  serverResults.value = result.results || []
  step.value = 'SERVER_RESULT'
}

async function commitImport() {
  committing.value = true
  try {
    importResult.value = await catalogApi.commit({ shopId: props.shopId, products: products.value.map(toApiProduct) }, crypto.randomUUID())
    step.value = 'RESULT'
  } finally {
    committing.value = false
  }
}

function toApiProduct(p) {
  return {
    listingMode: p.listingMode, shopId: p.shopId, listingId: p.listingId,
    name: p.name, slug: p.slug, description: p.description,
    listingType: p.listingType, currency: p.currency, basePrice: p.basePrice,
    memberPrice: p.memberPrice, promotionPrice: p.promotionPrice,
    promotionStartsAt: p.promotionStartsAt, promotionEndsAt: p.promotionEndsAt,
    negotiable: p.negotiable, groupBuyEnabled: p.groupBuyEnabled,
    media: p.media.map(m => ({ mediaType: m.mediaType, url: m.url, mainImage: m.mainImage, sortOrder: m.sortOrder, uploadFile: m.file || null })),
    variants: p.variants.map(v => ({
      sku: v.sku, name: v.name, attributesJson: v.attributesJson, stockQuantity: v.stockQuantity,
      images: v.images.map(m => ({ mediaType: m.mediaType, url: m.url, mainImage: m.mainImage, sortOrder: m.sortOrder, uploadFile: m.file || null }))
    }))
  }
}

function bool(v) { return ['true', '1', 'yes', 'y'].includes(String(v).toLowerCase()) }
function numberOrNull(v) { const n = Number(v); return v === '' || v == null || Number.isNaN(n) ? null : n }
function slugify(v) { return String(v || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') }
function finish() {
  if (props.f7router) props.f7router.back()
}

function downloadTemplate() {
  const csv = 'listingMode,shopId,listingId,productName,slug,description,listingType,currency,basePrice,memberPrice,promotionPrice,promotionStartsAt,promotionEndsAt,groupBuyEnabled,negotiable,variantSku,variantName,variantAttributes,stockQuantity,mainImageUrl,variantImageUrls,groupBuyTiers\nPRODUCT,,,,,,,,,,,,,,,,,,,,;\n'
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'avocash-catalog-template.csv'
  a.click()
  URL.revokeObjectURL(url)
}
</script>

<style scoped>
.hidden-input { display: none; }
.drop-zone { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 28px; margin-top: 18px; border: 2px dashed #b9c5d2; border-radius: 16px; text-align: center; cursor: pointer; }
.summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); text-align: center; gap: 8px; }
.summary-grid div { display: flex; flex-direction: column; }
.summary-grid strong { font-size: 22px; }
.media-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.media-tile { display: flex; flex-direction: column; gap: 4px; width: 94px; }
.media-tile img { width: 94px; height: 94px; object-fit: cover; border-radius: 10px; }
.footer-actions { display: flex; justify-content: space-between; margin-top: 24px; }
</style>
